import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { getFormEntriesByFormName } from "../services/formServices";
import toast from "react-hot-toast";
import { FORMNAMES } from "../utilities/codes";
import { schoolContext } from "../contexts/SchoolContextProvider";
import { mediaSource, useBackgroundImage } from "../utilities/mediautilities";
import { useTranslation } from "react-i18next";
import { I18N_KEYS } from "../languages/PlaceHolder.js";
import { MdWork, MdSchool, MdAccessTime } from "react-icons/md";

const Dignitary = () => {
    const [selectedCard, setSelectedCard] = useState(null);
    const { schoolDetails } = useContext(schoolContext);
    const [dignitories, setDignitories] = useState([])
    const { t } = useTranslation()
    const bgUrl = useBackgroundImage(schoolDetails?.dignataries_back_image?.fileUrl);

    useEffect(() => {
        getDignatories();
    }, [])

    const getDignatories = async () => {
        try {
            const response = await getFormEntriesByFormName(FORMNAMES.SCHOOLDIGNATORIES);
            setDignitories(response);
        } catch (error) {
            toast.error(error.message);
        }
    }

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.15 }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeOut" }
        }
    };

    return (
        <>
            {dignitories?.length > 0 && (
                <>
                    <section
                        id="dignitaries"
                        className="py-20 px-6 relative bg-fixed bg-center bg-cover"
                        style={{ backgroundImage: `url(${bgUrl})` }}
                    >
                        <div className="absolute inset-0 bg-black/70"></div>

                        <div className="relative z-10 common-container mx-auto">
                            {/* Heading */}
                            <motion.h2
                                initial={{ opacity: 0, y: -30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                                className="text-4xl font-bold text-center text-accent"
                            >
                                {t(I18N_KEYS.DIGNATARIESTITLE)}
                            </motion.h2>

                            <motion.p
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.3 }}
                                className="text-accent mt-3 mb-14 text-center text-lg"
                            >
                                {schoolDetails?.dignitaries_subtitle}
                            </motion.p>

                            {/* Cards */}
                            <motion.div
                                variants={containerVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10"
                            >
                                {dignitories?.map((dig, index) => (
                                    <motion.div
                                        key={index}
                                        variants={cardVariants}
                                        whileHover={{ scale: 1.05 }}
                                        onClick={() => setSelectedCard(dig)}
                                        className="bg-white/90 backdrop-blur-lg shadow-md hover:shadow-2xl rounded-3xl text-center p-7 transition-all duration-300 
                                                   cursor-pointer border border-gray-100 group hover:-translate-y-2"
                                    >
                                        {/* Image */}
                                        <div className="h-44 w-44 mx-auto rounded-full overflow-hidden mb-5 border-[5px] border-primary/40 shadow-lg group-hover:border-primary transition">
                                            <img
                                                src={mediaSource(
                                                    dig?.payload?.profile_photo?.fileUrl
                                                )}
                                                alt={dig?.payload?.role}
                                                className="h-full w-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                                            />
                                        </div>

                                        {/* Content */}
                                        <div className="space-y-2">
                                            {dig?.payload?.fullname && (
                                                <h3 className="text-lg font-extrabold capitalize text-primary">
                                                    {dig?.payload?.fullname}
                                                </h3>
                                            )}

                                            <div className="grid grid-cols-2">
                                                {dig?.payload?.role && (
                                                    <div className="flex items-center justify-center gap-2 text-primary text-sm">
                                                        <MdWork />
                                                        <span>{dig?.payload?.role}</span>
                                                    </div>
                                                )}

                                                {dig?.payload?.Qualification && (
                                                    <div className="flex items-center justify-center gap-2 text-primary text-sm">
                                                        <MdSchool />
                                                        <span>{dig?.payload?.Qualification}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {dig?.payload?.Experience && (
                                                <div className="flex items-center justify-center gap-2 text-primary text-sm mt-2">
                                                    <MdAccessTime />
                                                    <span>
                                                        {dig?.payload?.Experience}{" "}
                                                        {t(I18N_KEYS.YEARSOFEXP)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </section>

                    {/* MODAL */}
                    <AnimatePresence>
                        {selectedCard && (
                            <motion.div
                                className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setSelectedCard(null)}
                            >
                                <button
                                    onClick={() => setSelectedCard(null)}
                                    className="absolute top-5 right-5 bg-white rounded-full w-11 h-11 shadow-xl text-xl font-bold hover:bg-primary hover:text-white"
                                >
                                    &times;
                                </button>

                                <motion.div
                                    className="bg-white rounded-[28px] shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.9, opacity: 0 }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="p-8 space-y-6 text-center">
                                        <div className="inline-flex flex-col space-y-4">
                                            <img
                                                src={mediaSource(
                                                    selectedCard?.payload?.profile_photo?.fileUrl
                                                )}
                                                alt="Profile-image"
                                                className="h-60 w-60 rounded-xl"
                                            />
                                        </div>

                                        <div className="flex flex-col sm:ml-8 sm:mr-8 md:ml-12 md:mr-12 space-y-2">
                                            <div className=" flex items-center justify-between">
                                                <h2 className="text-lg font-extrabold  text-primary capitalize">
                                                    {selectedCard?.payload?.fullname}
                                                </h2>

                                                {selectedCard?.payload?.role && (
                                                    <div className="flex items-center gap-2 text-primary">
                                                        <MdWork />
                                                        <span className="text-sm md:text-base">
                                                            {selectedCard?.payload?.role}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {selectedCard?.payload?.Qualification && (
                                                <div className="flex items-center justify-start gap-2 text-secondary text-left ">
                                                    <MdSchool />
                                                    <span>
                                                        {selectedCard?.payload?.Qualification}{" - "}
                                                        {selectedCard?.payload?.Experience}{" "}
                                                        {t(I18N_KEYS.YEARSOFEXP)}
                                                    </span>
                                                </div>
                                            )}

                                            {selectedCard?.payload?.about &&
                                                (<p
                                                    className="text-secondary text-sm text-left"
                                                    dangerouslySetInnerHTML={{ __html: selectedCard?.payload?.about }}
                                                />)}
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </>
            )
            }
        </>

    );
}

export default Dignitary;
